import React, { useEffect, useRef, useState } from "react";
import { setThemeColors } from "src/helpers/common";
import { generateTheme } from "src/helpers/theme";
import { useAutoBlurOnInteraction } from "src/hooks/useAutoBlurOnInteraction";
import useTheme from "src/hooks/useTheme";

interface LayoutProps {
  className?: string; // extra classes for outer shell
  containerClassName?: string; // extra classes for inner max-width container
  header?: React.ReactNode;
  footer?: React.ReactNode;
  stickyHeader?: boolean;
  stickyFooter?: boolean;
  children: React.ReactNode;

  enableTheme?: boolean;
  supportProgramKey?: string;
}

const Layout: React.FC<LayoutProps> = ({
  className,
  containerClassName,
  header,
  footer,
  stickyHeader = false,
  stickyFooter = false,
  children,

  enableTheme = false,
  supportProgramKey,
}) => {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  // Blur active input when user taps outside within this shell
  useAutoBlurOnInteraction({ rootRef: shellRef });

  // theme hook
  const theme = useTheme(supportProgramKey);

  // set theme colors
  useEffect(() => {
    if (enableTheme && theme) {
      // setThemeColors(config?.theme?.colors);
      setThemeColors(generateTheme(theme?.colors));
    }
  }, [enableTheme, theme]);

  // Measure header / footer heights for clipping the scroll area
  useEffect(() => {
    const measure = () => {
      if (stickyHeader && headerRef.current) {
        setHeaderHeight(headerRef.current.clientHeight);
      } else {
        setHeaderHeight(0);
      }

      if (stickyFooter && footerRef.current) {
        setFooterHeight(footerRef.current.clientHeight);
      } else {
        setFooterHeight(0);
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [stickyHeader, stickyFooter]);

  return (
    <div
      ref={shellRef}
      className={`
        fixed inset-0
        flex justify-center
        bg-slate-100
        ${className ?? ""}
      `}
    >
      {/* Inner bounded column */}
      <div
        className={`
          relative
          w-full max-w-lg
          ${containerClassName ?? ""}
        `}
      >
        {/* STICKY HEADER */}
        {stickyHeader && header && (
          <header
            ref={headerRef}
            className="
              fixed top-0 left-1/2 -translate-x-1/2
              w-full max-w-lg
              z-20
            "
          >
            {header}
          </header>
        )}

        {/* STICKY FOOTER */}
        {stickyFooter && footer && (
          <footer
            ref={footerRef}
            className="
              fixed bottom-0 left-1/2 -translate-x-1/2
              w-full max-w-lg
              z-20
            "
          >
            {footer}
          </footer>
        )}

        {/* SCROLLABLE CONTENT AREA, CLIPPED BETWEEN HEADER & FOOTER */}
        <main
          className="
            absolute left-0 right-0
            overflow-y-auto
          "
          style={{
            top: stickyHeader && header ? headerHeight : 0,
            bottom: stickyFooter && footer ? footerHeight : 0,
          }}
        >
          {/* Non-sticky header inside flow */}
          {!stickyHeader && header && <header ref={headerRef}>{header}</header>}

          {/* px-4 py-4 removed from below section tag to prevent extra padding */}
          <section className="h-full flex flex-col">{children}</section>

          {/* Non-sticky footer inside flow */}
          {!stickyFooter && footer && <footer ref={footerRef}>{footer}</footer>}
        </main>
      </div>
    </div>
  );
};

export default Layout;
